/**
 * @description API service for payment sessions and stored card management.
 * Handles HPF session lifecycle, card vaulting, and stored card CRUD operations.
 *
 * Key features:
 * - Payment session creation (Order → HPF Session)
 * - Card vaulting after HPF capture
 * - Stored card list, get, set-default, remove
 * - HPF client config retrieval (publicKey, scriptUrl)
 *
 * @dependencies
 * - apiClient: Axios instance with auth headers and company context
 *
 * @notes
 * - All endpoints require auth + company context (set by apiClient interceptor)
 * - Amount is always in cents (integer) — backend converts to dollar strings for EPG
 * - Card responses use camelCase (transformed by backend PaymentVaultService)
 * - This service is for the Elavon HPF flow. The legacy PaymentCardForm +
 *   PaymentTokenizationService flow remains for non-HPF gateways.
 */

import apiClient from './apiClient';

const paymentSessionService = {
  // ==========================================================================
  // HPF Client Configuration
  // ==========================================================================

  /**
   * Get gateway client config for HPF SDK initialization.
   * Returns publicKey, hpfScriptUrl, environment — never secretKey.
   * @returns {Promise<object>} - { publicKey, hpfScriptUrl, environment, gatewayType, features }
   */
  getClientConfig: async () => {
    const response = await apiClient.get('/payment-sessions/client-config');
    return response.data;
  },

  // ==========================================================================
  // Payment Session Lifecycle
  // ==========================================================================

  /**
   * Create a payment session for HPF card capture.
   * Backend creates EPG Order + Payment Session.
   * @param {object} data - Session creation data
   * @param {number} data.amount - Amount in cents (positive integer)
   * @param {string} [data.currency='USD'] - ISO 4217 currency code
   * @param {string} [data.description] - Payment description
   * @returns {Promise<object>} - { sessionId, sessionUrl, orderId, expiresAt }
   */
  createPaymentSession: async (data) => {
    const response = await apiClient.post('/payment-sessions', data);
    return response.data;
  },

  /**
   * Vault a HostedCard from HPF into a StoredCard.
   * Called after HPF messageHandler fires with hostedCardId.
   * @param {string} sessionId - Payment session ID
   * @param {object} data - Vault data
   * @param {string} data.hostedCardId - HostedCard ID from HPF messageHandler
   * @param {number} data.customerId - Customer integer ID
   * @param {object} [data.customerData] - Optional customer info (fullName, email)
   * @returns {Promise<object>} - { storedCard: { id, lastFour, cardBrand, ... } }
   */
  vaultCard: async (sessionId, data) => {
    const response = await apiClient.post(`/payment-sessions/${sessionId}/vault`, data);
    return response.data;
  },

  // ==========================================================================
  // Stored Card Management
  // ==========================================================================

  /**
   * List all stored cards for a customer.
   * @param {number} customerId - Customer integer ID
   * @returns {Promise<object>} - { cards: Array<StoredCard> }
   */
  getCustomerCards: async (customerId) => {
    const response = await apiClient.get(`/stored-cards/customer/${customerId}`);
    return response.data;
  },

  /**
   * Get a single stored card by ID.
   * @param {string} cardId - Card UUID
   * @returns {Promise<object>} - { card: StoredCard }
   */
  getCard: async (cardId) => {
    const response = await apiClient.get(`/stored-cards/${cardId}`);
    return response.data;
  },

  /**
   * Set a card as the customer's default.
   * @param {string} cardId - Card UUID
   * @param {number} customerId - Customer integer ID
   * @returns {Promise<object>} - { card: StoredCard }
   */
  setDefaultCard: async (cardId, customerId) => {
    const response = await apiClient.put(`/stored-cards/${cardId}/default`, { customerId });
    return response.data;
  },

  /**
   * Remove a stored card (gateway delete + local deactivation).
   * @param {string} cardId - Card UUID
   * @returns {Promise<object>} - { message, card: StoredCard }
   */
  removeCard: async (cardId) => {
    const response = await apiClient.delete(`/stored-cards/${cardId}`);
    return response.data;
  }
};

export default paymentSessionService;
